import React, { useMemo, useState, useRef, useEffect } from "react";
import ScriptComponent from "../../components/ScriptComponent";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import { mapQuoteDataToScriptProps } from "./utils";
import { recentScripMetaParts, recentScripSide } from "./recentScripsStorage";
import styles from "./OrderInitiatePage.module.css";

const DocIcon = () => (
  <svg
    className={styles.scripSelectionTitleIcon}
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="12"
    viewBox="0 0 10 12"
    fill="none"
    aria-hidden
  >
    <path
      d="M2.33333 8.16667H7V9.33333H2.33333V8.16667ZM2.33333 5.83333H7V7H2.33333V5.83333ZM5.83333 0H1.16667C0.525 0 0 0.525 0 1.16667V10.5C0 11.1417 0.519167 11.6667 1.16083 11.6667H8.16667C8.80833 11.6667 9.33333 11.1417 9.33333 10.5V3.5L5.83333 0ZM8.16667 10.5H1.16667V1.16667H5.25V4.08333H8.16667V10.5Z"
      fill="currentColor"
    />
  </svg>
);

const ScriptComponentWrapper = React.memo(
  React.forwardRef(
    (
      {
        id,
        index,
        dropdownOptions,
        selection,
        onSelectionChange,
        onDelete,
        onAddScript,
        animateIn,
        quoteData,
        selected,
        onSelectedChange,
        validityOptions,
      },
      ref
    ) => {
      const quoteProps = useMemo(
        () => mapQuoteDataToScriptProps(quoteData, selection?.exchange),
        [quoteData, selection?.exchange]
      );
      return (
        <div ref={ref}>
          <ScriptComponent
            id={id}
            scriptIndex={index}
            dropdownOptions={dropdownOptions}
            selection={selection}
            onSelectionChange={onSelectionChange}
            onDelete={onDelete}
            onAddScript={onAddScript}
            animateIn={animateIn}
            selected={selected}
            onSelectedChange={onSelectedChange}
            validityOptions={validityOptions}
            {...quoteProps}
          />
        </div>
      );
    }
  )
);

function ScriptListSection({
  scriptIds,
  lastAddedScriptId,
  lastAddedScriptRef,
  scriptDropdowns,
  scriptSelections,
  quoteDataList,
  selectedScriptIds,
  validityOptions,
  onAddScrip,
  onAddRecentScrip,
  recentScrips = [],
  onDeleteScrip,
  onScriptSelectionChange,
  onScriptSelectedChange,
}) {
  const scrollClassName = scriptIds.length > 2 ? styles.scripSelectionScrollInner : undefined;
  const [isRecentOpen, setIsRecentOpen] = useState(false);
  const recentWrapRef = useRef(null);
  const canAdd = scriptIds.length < 10;

  useEffect(() => {
    if (!isRecentOpen) return;
    const handleClickOutside = (event) => {
      if (recentWrapRef.current && !recentWrapRef.current.contains(event.target)) {
        setIsRecentOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setIsRecentOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [isRecentOpen]);

  const handlePickRecent = (entry) => {
    onAddRecentScrip?.(entry);
    setIsRecentOpen(false);
  };

  return (
    <section className={styles.scripSelectionContainer}>
      <div className={styles.scripSelectionHeader}>
        <h2 className={styles.scripSelectionTitle}>
          <DocIcon />
          SCRIP SELECTION
        </h2>
        <div
          className={`${styles.recentScripWrap}${isRecentOpen ? ` ${styles.recentScripWrapOpen}` : ""}`}
          ref={recentWrapRef}
        >
          <button
            type="button"
            className={styles.recentScripTrigger}
            aria-expanded={isRecentOpen}
            aria-haspopup="listbox"
            onClick={() => setIsRecentOpen((o) => !o)}
          >
            <span className={styles.recentScripTriggerLabel}>Recent scrips</span>
            <GlobalIcon iconName="ChevronDownIcon" width={14} height={14} aria-hidden />
          </button>
          {isRecentOpen ? (
            <div className={styles.recentScripDropdown} role="listbox" aria-label="Recent scrips">
              {recentScrips.length === 0 ? (
                <span className={styles.recentScripEmpty}>No recent scrips yet</span>
              ) : (
                recentScrips.map((entry) => {
                  const metaParts = recentScripMetaParts(entry);
                  const side = recentScripSide(entry);
                  const rowDisabled = !canAdd && scriptIds.length > 1;
                  return (
                    <button
                      key={entry.name}
                      type="button"
                      role="option"
                      className={styles.recentScripItem}
                      disabled={rowDisabled}
                      onClick={() => handlePickRecent(entry)}
                    >
                      <span className={styles.recentScripItemTop}>
                        <span className={styles.recentScripItemName}>{entry.name}</span>
                        <span
                          className={`${styles.recentScripSideTag} ${
                            side === "Sell" ? styles.recentScripSideTagSell : styles.recentScripSideTagBuy
                          }`}
                        >
                          {side}
                        </span>
                      </span>
                      {metaParts.length > 0 ? (
                        <span className={styles.recentScripItemMeta}>
                          {metaParts.map((part, idx) => (
                            <span key={`${part}-${idx}`} className={styles.recentScripItemChip}>
                              {part}
                            </span>
                          ))}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </div>
      </div>
      <div className={scrollClassName}>
        <div className={styles.scriptList}>
          {scriptIds.map((id, index) => (
            <ScriptComponentWrapper
              key={id}
              ref={id === lastAddedScriptId ? lastAddedScriptRef : null}
              id={id}
              index={index}
              dropdownOptions={scriptDropdowns?.quote_drop_down_options_list?.[index]}
              selection={scriptSelections[index]}
              onSelectionChange={onScriptSelectionChange}
              onDelete={scriptIds.length > 1 ? onDeleteScrip : undefined}
              onAddScript={onAddScrip}
              animateIn={id === lastAddedScriptId}
              quoteData={quoteDataList[index]}
              selected={selectedScriptIds.has(id)}
              onSelectedChange={(checked) => onScriptSelectedChange(id, checked)}
              validityOptions={validityOptions}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default React.memo(ScriptListSection);
